import { brand } from '@/utils/brand'
import { getV5ApiUrl } from '@/utils/urlHelpers'
import { format, parseISO } from 'date-fns'
import { OrderTrackingButtonItem } from 'lib/components/OrderTracking/orderTrackingButtons'
import { PromoCode } from 'lib/components/PaymentDetails/partials/PromoCodeInput'
import { RouteSnapshotTravelerShipment } from 'lib/components/RouteSnapshot/RouteSnapshot'
import { ShipmentDetailsModalProps } from 'lib/components/ShipmentDetailsModal/ShipmentDetailsModal'
import { blankShipmentPayload } from 'lib/mock/tracking_results/mockOrderData'

export const formatTrackingIdLink = (trackingNumber: string): string => {
  return `/order-tracking?tracking_id=${trackingNumber}`
}

// Mapping function to convert GraphQL order to standard Order interface
function mapGraphQLOrderToInternal(apiOrder: ApiOrder): Order {
  if (!apiOrder) {
    return null as unknown as Order
  }
  // Helper function to parse date strings with fallback
  const parseDate = (dateString: string | null | undefined): string => {
    if (!dateString) return new Date().toISOString()
    const parsed = new Date(dateString)
    return isNaN(parsed.getTime())
      ? new Date().toISOString()
      : parsed.toISOString()
  }

  // Helper function to format date as MM/DD/YYYY using UTC semantics
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return ''

    let date: Date

    try {
      // If the date string contains a T, parse as ISO (timestamp)
      if (dateString.includes('T')) {
        date = parseISO(dateString)
      } else {
        // Parse date-only string (YYYY-MM-DD) as local midnight
        date = parseISO(`${dateString}T00:00:00`)
      }

      if (isNaN(date.getTime())) return ''

      // Format as MM/dd/yyyy with date-fns format
      return format(date, 'MM/dd/yyyy')
    } catch {
      return ''
    }
  }

  // Helper function to map carrier system name to shipper name
  const getShipperName = (carrierSystemName: string): Shipper => {
    const shipperMap: Record<string, Shipper> = {
      FEDEX_EXPRESS: 'fedex',
      FEDEX_GROUND: 'fedex',
      UPS: 'ups',
      USPS: 'usps',
      DHL: 'dhl',
    }
    return shipperMap[carrierSystemName] || 'fedex'
  }

  // Helper function to map GraphQL address to OrderAddress
  const mapAddress = (address: ApiAddress): OrderAddress => {
    return {
      id: address.id || '',
      address_1: address.address1 || '',
      address_2: address.address2 || undefined,
      city: address.city || '',
      state: address.state || '',
      zip: address.zip5 || '',
      country: address.countryCode || '',
      country_code: address.countryCode || undefined,
      phone_number: '', // Not available in GraphQL
      company_name: address.companyName || undefined,
      attention_name: address.attentionName || undefined,
      cruise: address.cruise || undefined,
    }
  }

  // Helper function to get payment method info
  const getPaymentMethod = (
    transactions: ApiTransaction[],
  ): OrderPaymentMethod => {
    const successfulTransaction = transactions.find(
      (t) => t.state === 'successful' && t.paymentMethod,
    )

    if (successfulTransaction?.paymentMethod) {
      const paymentMethod = successfulTransaction.paymentMethod
      // Handle OtherPaymentMethod (Apple Pay, PayPal, etc.)
      if ('type' in paymentMethod && 'description' in paymentMethod) {
        const name =
          paymentMethod.type === 'apple_pay'
            ? 'Apple Pay'
            : paymentMethod.type === 'paypal'
              ? 'PayPal'
              : paymentMethod.type
        return {
          name: name,
          display_name: name,
          description: paymentMethod.description || '',
        } as OtherPaymentMethod
      }

      // Handle BraintreePaymentMethod (credit/debit cards)
      if ('cardBrand' in paymentMethod && 'last4' in paymentMethod) {
        return {
          card_brand: paymentMethod.cardBrand || 'Unknown',
          last4: paymentMethod.last4 || '0000',
          created_at: successfulTransaction.createdAt || '',
          display_name: `${paymentMethod.cardBrand} ending in ${paymentMethod.last4}`,
        } as CreditCardPaymentMethod
      }

      // Handle ClubBillingPaymentMethod
      if ('facility' in paymentMethod && 'accountNumber' in paymentMethod) {
        return {
          name: paymentMethod.facility.name || 'Club Billing',
          display_name: paymentMethod.facility.name,
          account_number: paymentMethod.accountNumber || '',
        } as ClubBillingPaymentMethod
      }
    }

    // Default fallback
    return {
      card_brand: 'Unknown',
      last4: '0000',
      created_at: '',
      display_name: 'Unknown Payment Method',
    } as CreditCardPaymentMethod
  }

  // Map user info
  const user: OrderUser = {
    id: apiOrder.user?.id || '',
    first_name: apiOrder.user?.firstName || undefined,
    last_name: apiOrder.user?.lastName || undefined,
    email: apiOrder.user?.email || undefined,
    phone_number: apiOrder.user?.phoneNumber || undefined,
    mobile_verification_eligible:
      apiOrder.user?.mobileVerificationEligible || false,
    mobile_verified: apiOrder.user?.mobileVerified || false,
  }

  // Group shipments by leg and traveler, then map to OrderShipment format
  const shipmentsByLeg = (apiOrder.shipments || []).reduce(
    (acc, shipment) => {
      const legIndex = shipment.leg?.legIndex || 1
      if (!acc[legIndex]) {
        acc[legIndex] = {}
      }

      const travelerId = shipment.traveler?.id || 'unknown'
      if (!acc[legIndex][travelerId]) {
        acc[legIndex][travelerId] = {
          id: travelerId,
          name: shipment.traveler?.name || 'Unknown Traveler',
          email: shipment.traveler?.email || '',
          phone: shipment.traveler?.phone || '',
          sms: false,
          shipments: [],
        }
      }

      // Create OrderShipment from GraphQL shipment
      const orderShipment: OrderShipment = {
        id: shipment.trackingId || '',
        name: shipment.product?.displayName || 'Unknown Item',
        carrier_tracking_id: shipment.carrierTrackingId || '',
        tracking_id: shipment.trackingId || '',
        ship_date: formatDate(shipment.shipDate),
        arrival_date: formatDate(shipment.arrivalDate),
        estimated_carrier_delivery_date: formatDate(shipment.arrivalDate),
        updated_at: formatDate(shipment.updatedAt || shipment.arrivalDate),
        humanized_carrier_service_type:
          shipment.carrierServiceLevel?.displayName || 'Unknown Service',
        cruise_session: shipment.cruiseSession
          ? {
              cruise_line: shipment.cruiseSession.cruiseLine ?? null,
              ship_name: shipment.cruiseSession.shipName ?? null,
              cabin: shipment.cruiseSession.cabin ?? null,
              reservation_number:
                shipment.cruiseSession.reservationNumber ?? null,
              embarkation_port: shipment.cruiseSession.embarkationPort ?? null,
              disembarkation_port:
                shipment.cruiseSession.disembarkationPort ?? null,
              embarkation_date: shipment.cruiseSession.embarkationDate ?? null,
              disembarkation_date:
                shipment.cruiseSession.disembarkationDate ?? null,
            }
          : null,
        products: [
          {
            type: 'label' as const,
            id: shipment.product?.id || '',
            display_name: shipment.product?.displayName || 'Unknown Item',
            full_name:
              shipment.product?.name ||
              shipment.product?.displayName ||
              'Unknown Item',
            name: shipment.product?.name || undefined,
            weight: shipment.product?.weight || 0,
            product_line: {
              id: shipment.product?.productLine?.id || '',
              display_name:
                shipment.product?.productLine?.displayName || 'Unknown Type',
            },
            packagingType: shipment.packaging?.name || undefined,
          } as OrderProductLabel,
        ],
        carrier_service_level: {
          id: shipment.carrierServiceLevel?.id || '',
          display_name:
            shipment.carrierServiceLevel?.displayName || 'Unknown Service',
          carrier: {
            code: getShipperName(
              shipment.carrierServiceLevel?.carrier?.systemName ||
                'FEDEX_EXPRESS',
            ),
            system_name:
              shipment.carrierServiceLevel?.carrier?.systemName ||
              'FEDEX_EXPRESS',
            display_name:
              shipment.carrierServiceLevel?.carrier?.displayName || 'FedEx',
          },
        },
        discount_cents: 0,
        wholesale_discount_cents: 0,
        upgrade_discount: 0,
        retail_ship_rate: 0,
        paid_insurance_cents: 0,
        handling_fee_cents: 0,
        fuel_surcharge_fee_cents: 0,
        clearance_fee_cents: 0,
        total_price_cents: 0, // Not available in GraphQL response
        state: convertShipmentState(shipment.state) || undefined,
        s3_label_url: undefined,
      }

      acc[legIndex][travelerId].shipments.push(orderShipment)
      return acc
    },
    {} as Record<number, Record<string, OrderLegTraveler>>,
  )

  // Map legs
  const legs: OrderLeg[] = (apiOrder.legs || []).map((leg) => {
    const travelers = shipmentsByLeg[leg.legIndex]
      ? Object.values(shipmentsByLeg[leg.legIndex])
      : []

    return {
      leg_id: leg.id || `leg-${leg.legIndex}`,
      pickup: leg.pickup || false,
      uber_pickup: leg.uberPickup || false,
      pickup_ready_time: leg.pickupReadyTime,
      pickup_close_time: leg.pickupCloseTime,
      leg_name: leg.legIndex === 1 ? 'Outbound' : 'Return',
      origin: mapAddress(leg.shipRoute?.origin),
      destination: mapAddress(leg.shipRoute?.destination),
      pickup_fee_cents: undefined,
      travelers,
      leg_index: leg.legIndex as number,
    }
  })

  // Map transactions
  const transactions: OrderTransaction[] = (apiOrder.transactions || []).map(
    (transaction) => ({
      type: transaction.type || '',
      transaction_id: transaction.transactionId || '',
      value: transaction.value || 0,
      payer_name: transaction.payerName || '',
      payer_email: transaction.payerEmail || '',
      state: transaction.state || '',
      payment_method: getPaymentMethod([transaction]),
    }),
  )

  // Return the mapped Order
  return {
    id: apiOrder.id || '',
    total_price_cents: apiOrder.totalPriceCents || 0,
    order_id: apiOrder.orderId || apiOrder.id || '',
    state: apiOrder.state || '',
    receipt_views: apiOrder.receiptViews || 0,
    device: apiOrder.device || '',
    requires_travel_documents: apiOrder.requiresTravelDocuments === true,
    user_id: user.id,
    created_at: parseDate(apiOrder.createdAt),
    updated_at: parseDate(apiOrder.updatedAt || apiOrder.createdAt),
    requires_mobile_verification: Boolean(apiOrder.requiresMobileVerification),
    brand: apiOrder.brand
      ? { user_edit_order_enabled: apiOrder.brand.userEditOrderEnabled }
      : undefined,
    user,
    legs,
    transactions,
  }
}

function convertShipmentState(state: string): string | undefined {
  // Map GraphQL shipment states to internal shipment status types
  const stateMap: Record<string, string> = {
    // GraphQL states -> Internal status keys
    accepted: 'pending', // Order accepted, pending shipment
    cancelled: 'cancelled', // Cancelled orders are cancelled
    delivered: 'delivered', // Direct mapping
    rejected: 'rejected', // Rejected orders are rejected
    in_transit: 'in_transit', // Direct mapping
    'in-transit': 'in_transit', // Handle both formats
    enqueued: 'pending', // Queued for processing
  }

  const normalizedState = state.toLowerCase().trim()
  return stateMap[normalizedState]
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) return ''

  try {
    // Handle different date formats from API
    const date = new Date(dateString)
    return date.toISOString().split('T')[0] // Returns YYYY-MM-DD format
  } catch {
    return dateString // Return as-is if parsing fails
  }
}

export const extractYear = (
  result: TrackingResult | null | undefined,
): string => {
  if (!result || typeof result !== 'object') {
    return new Date().getFullYear().toString()
  }

  const dateFields = [
    { value: result.tracked_date, pattern: /^(\d{4})-/ },
    { value: result.shipped_on_date, pattern: /(\d{4})$/ },
    { value: result.scheduled_deliver_date, pattern: /(\d{4})$/ },
    { value: result.last_updated_at, pattern: /(\d{4})/ },
  ]

  for (const field of dateFields) {
    if (typeof field.value === 'string') {
      const match = field.value.match(field.pattern)
      if (match?.[1]) {
        return match[1]
      }
    }
  }

  return new Date().getFullYear().toString()
}

const mapTrackingProgressToUpdates = (result: TrackingResult) => {
  if (!result) return []

  const progress = result?.progress
  if (!progress || !Array.isArray(progress)) return []

  const year = extractYear(result)

  return progress
    .map((item) => {
      // Combine view_date and view_time into a proper date_time format with year
      let dateTime = ''
      if (item.view_date && item.view_time) {
        // Add year to the date (view_date format: "Aug 10", "Aug 13", etc.)
        dateTime = `${item.view_date} ${year}, ${item.view_time}`
      } else if (item.view_date) {
        dateTime = `${item.view_date} ${year}`
      }

      return {
        title: item.user_message || '',
        date_time: dateTime,
        location: item.location || '',
      }
    })
    .reverse() // Reverse to show oldest first (like in your example)
}

// Function to map shipment API response to ShipmentDetailsModalProps format
const extractShipmentName = (apiData: any, labelProduct: any): string => {
  return (
    labelProduct?.full_name ||
    apiData.full_name ||
    apiData.name ||
    'Standard shipment'
  )
}

const normalizeShipperName = (carrier: any): Shipper => {
  const systemName = carrier?.system_name?.toLowerCase()
  if (systemName) {
    return systemName.replace(/_express|_/g, '') as Shipper
  }
  return carrier?.display_name?.toLowerCase().split(' ')[0] || 'fedex'
}

const extractAddress = (addressData: any) => ({
  address_1: addressData?.address_1 || '',
  address_2: addressData?.address_2 || undefined,
  city: addressData?.city || '',
  state: addressData?.state || '',
  zip: addressData?.zip || '',
  country: addressData?.country || 'United States',
  company_name: addressData?.company_name || undefined,
  attention_name: addressData?.attention_name || undefined,
})

export const mapShipmentApiToDetails = (
  apiData: any,
): Omit<ShipmentDetailsModalProps, 'id'> => {
  // Extract the label product (main shipment item)
  const labelProduct = apiData.products?.find(
    (product: any) => product.type === 'label',
  )
  return {
    name: extractShipmentName(apiData, labelProduct),
    pickup: apiData?.leg?.pickup,
    pickup_date:
      formatDate(apiData.ship_date) || formatDate(apiData.created_at) || '',
    delivery_by: formatDate(apiData.arrival_date) || '',
    service:
      apiData.humanized_carrier_service_type ||
      apiData.carrier_service_level?.display_name ||
      apiData.carrier_service_level?.service_level?.display_name ||
      'Standard Service',
    insurance_cents: apiData.coverage_amount_cents || 0,
    status: apiData.state || 'delivered',
    shipper: normalizeShipperName(apiData.carrier),
    origin: extractAddress(apiData.origin),
    destination: extractAddress(apiData.destination),
    // Either end can carry a `cruise` flag when it's a ship/port — the
    // destination on an outbound (to-cruise) shipment, the origin on a return
    // (from-cruise) one. The cruise session hangs off the shipment itself.
    originIsCruise: Boolean(apiData.origin?.cruise),
    destinationIsCruise: Boolean(apiData.destination?.cruise),
    cruise: apiData.cruise_session ?? undefined,
    updates: mapTrackingProgressToUpdates(
      apiData.tracking_result as TrackingResult,
    ),
  }
}

export const legShipper = (leg: OrderLeg): Shipper => {
  return (leg.travelers?.[0].shipments?.[0].carrier_service_level?.carrier
    ?.system_name || 'fedex') as Shipper
}

export const travelerShipments = (
  leg: OrderLeg,
): RouteSnapshotTravelerShipment[] => {
  if (!leg.travelers) return []

  return leg.travelers.map((traveler) => ({
    traveler: traveler.name,
    shipments: traveler.shipments,
  }))
}

export const getOrderIdFromUrl = (): string => {
  // Priority 1: Extract from URL parameter 'order_id'
  const searchParams = new URLSearchParams(window.location.search)
  const orderIdParam = searchParams.get('order_id')
  if (orderIdParam) {
    return orderIdParam
  }
  // we can search by shipment tracking id as well
  const trackingIdParam = searchParams.get('tracking_id')
  if (trackingIdParam) {
    return trackingIdParam
  }

  // Priority 2: Extract from hash (fallback)
  const hash = window.location.hash.slice(1)
  if (hash) {
    return hash
  }

  // Priority 3: Extract from path segments (for internal navigation)
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  const lastSegment = pathSegments[pathSegments.length - 1]

  if (
    pathSegments.length > 1 &&
    pathSegments[0] === 'order-tracking' &&
    lastSegment !== 'order-tracking'
  ) {
    return lastSegment
  }

  return ''
}

export const getPaymentDisplayText = (
  paymentMethod: OrderPaymentMethod,
): string => {
  // Handle CreditCardPaymentMethod
  if ('card_brand' in paymentMethod && 'last4' in paymentMethod) {
    return `${paymentMethod.card_brand} ${paymentMethod.last4}`
  }

  // Handle ClubBillingPaymentMethod
  if ('account_number' in paymentMethod) {
    const last4 = paymentMethod.account_number.slice(-4)
    return `${paymentMethod.name} ${last4}`
  }

  // Handle OtherPaymentMethod (Apple Pay, PayPal, etc.)
  if ('description' in paymentMethod) {
    return `${paymentMethod.name}`
  }

  return 'Unknown Payment Method'
}

export async function getShipmentDetails(
  authToken: string,
  tracking_id: string,
): Promise<Omit<ShipmentDetailsModalProps, 'id'>> {
  // only authenticated users can view shipment details
  const isLoggedIn = authToken && authToken.length > 0
  if (!isLoggedIn) {
    return blankShipmentPayload
  }

  try {
    // Fetch shipment details from API
    const response = await fetch(getV5ApiUrl(`/track/${tracking_id}`), {
      method: 'GET',
      headers: {
        'x-brand-id': brand.key,
        'x-user-token': authToken,
      } as HeadersInit,
    })

    if (!response.ok) {
      console.warn(
        `Failed to fetch shipment details: ${response.status} ${response.statusText}`,
      )
      return blankShipmentPayload
    }

    const apiData = await response.json()

    // Return undefined if no data found
    if (!apiData) {
      return blankShipmentPayload
    }

    // Use the mapper to transform API data to the expected format
    return mapShipmentApiToDetails(apiData)
  } catch (error) {
    console.error('Error fetching shipment details:', error)
    return blankShipmentPayload
  }
}

export { mapGraphQLOrderToInternal }

export const clickActionButton = (
  button: OrderTrackingButtonItem,
  orderIdParam: string,
  openModal?: (modalId: string, args?: Record<string, any>) => void,
) => {
  if (button.onClick) {
    // Direct onClick handler
    button.onClick(orderIdParam)
  } else if (button.path) {
    // Open URL in new tab (replace {order_id} placeholder)
    const url = button.path.replace('{order_id}', orderIdParam)
    window.open(url, '_blank')
  } else if (button.modal && openModal) {
    // Open modal dialog with orderId as args
    openModal(button.targetModal || '', { orderId: orderIdParam })
  }
}

export const formatOrderDate = (order: Order) => {
  const orderDate = new Date(order.created_at)
  return format(orderDate, 'MM/dd/yyyy  hh:mm a')
}

export const formatOrderTotal = (
  order: Order,
  locale: string,
  currency: string,
) => {
  return (order.total_price_cents / 100).toLocaleString(locale, {
    style: 'currency',
    currency: currency,
  })
}

export const getCompDiscounts = (promoCodes: PromoCode[] | undefined) => {
  if (!promoCodes) return []

  return promoCodes.filter((promoCode) => promoCode.promoType === 'comp')
}
